<template>
    <div class="column-chart-template">
        <canvas :id="'chart-'+id" :class="(if_chart_has_data ? '':'hide')" class="column-chart chart-template" ></canvas>
        <div :class="(!if_chart_has_data && !if_refreshing  ? 'd-inline-block':'hide')" class='text-center'>
            <h1 class="text-secondary mb-1"><i class="fas fa-chart-simple"></i></h1>
            <h4 class="text-secondary mb-2"><em>Press the <strong>Preview</strong> button to render the chart</em></h4>
        </div>
    </div>
</template>

<script>
import { defineComponent, markRaw } from 'vue';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import chartTooltip from '../../../../mixins/chart_tooltip';
import chartBase from '../../../../mixins/chart_base';

export default defineComponent({
    name: 'ColumnChartTemplateNew',
    mixins: [chartTooltip, chartBase],
    props: {
        chart_selected: {
            type: Object,
            required: true
        },
        id: {
            type: [String, Number],
            required: true
        },
        if_refreshing: {
            type: Boolean,
            default: false
        },
        if_on_report_page: {
            type: Boolean,
            default: false
        },
        if_benchmark_report: {
            type: Boolean,
            default: false
        },
        applied_chart_filter: {
            type: Object,
            default: () => ({})
        },
        if_show_label: {
            type: Boolean,
            default: false
        },
        size: {
            type: String,
            default: 'small'
        },
        section_id: {
            type: [String, Number],
            default: null
        },
        is_section_active: {
            type: Boolean,
            default: true
        },
        show_compressed_numbers: {
            type: Boolean,
            default: true
        },
        forceShowAllData: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            format_type: 'number',
            color: 'yellow',
            processed_data: null,
            // Preserve legend visibility across re-renders (e.g. pagination)
            datasetVisibility: null,
            /** When true, next chart render (e.g. after resize) uses no animation. Cleared after render. */
            skipAnimationForNextRender: false,
            chart_properties: {
                chart_type: 'column',
                chart_options: {
                    chart: {
                        type: 'column',
                        height: 350,
                        style: {
                            fontFamily: "'Nunito', sans-serif"
                        }
                    },
                    title: {
                        text: null
                    },
                    xAxis: {
                        categories: [],
                        labels: {
                            style: {
                                fontSize: '12px'
                            }
                        }
                    },
                    yAxis: {
                        title: {
                            text: null
                        },
                        labels: {
                            style: {
                                fontSize: '12px'
                            }
                        }
                    },
                    legend: {
                        enabled: true,
                        itemStyle: {
                            fontSize: '12px'
                        }
                    },
                    plotOptions: {
                        column: {
                            dataLabels: {
                                enabled: false
                            }
                        }
                    },
                    credits: {
                        enabled: false
                    },
                    tooltip: {
                        shared: true,
                        useHTML: true,
                        formatter: function() {
                            let tooltip = '<div class="chart-tooltip">';
                            if (this.points) {
                                this.points.forEach(function(point) {
                                    tooltip += `<div class="tooltip-item">
                                        <span class="tooltip-color" style="background-color: ${point.color}"></span>
                                        <span class="tooltip-label">${point.series.name}:</span>
                                        <span class="tooltip-value">${point.y}</span>
                                    </div>`;
                                });
                            }
                            tooltip += '</div>';
                            return tooltip;
                        }
                    }
                }
            }
        };
    },
    watch: {
        is_section_active: {
            handler(isActive) {
                if (!isActive) {
                    this.resetHoverState();
                }
            }
        },
        size(newSize, oldSize) {
            if (newSize !== oldSize) {
                this.skipAnimationForNextRender = true;
            }
        }
    },
    methods: {
        
        /**
         * Number of datasets currently visible. Uses chart legend state when chart exists, else all visible.
         */
        getVisibleDatasetCount() {
            if (this.chart?.data?.datasets && typeof this.chart.isDatasetVisible === 'function') {
                let count = 0;
                for (let i = 0; i < this.chart.data.datasets.length; i++) {
                    if (this.chart.isDatasetVisible(i)) count++;
                }
                return count || 1;
            }
            return this.num_of_datasets || 1;
        },
        /**
         * Whether dataset at index should be hidden. Uses stored legend state when valid, else none hidden.
         */
        isDatasetHiddenByDefault(index) {
            if (this.datasetVisibility && this.datasetVisibility.length === this.num_of_datasets && this.datasetVisibility[index] !== undefined) {
                return !this.datasetVisibility[index];
            }
            return false;
        },
        /**
         * Calculate how many labels (categories) to show based on chart size.
         * Uses visible dataset count so pagination matches legend state; large/x-large same chart width.
         */
        calculateLabelsToShow() {
            const chartSize = this.size || this.chart_selected?.size || 'small';
            const totalLabels = this.chart_selected.labels?.length || 0;
            const visibleDatasets = this.getVisibleDatasetCount();
            const totalDataPoints = totalLabels * visibleDatasets;
            
            if (chartSize === 'small') {
                const labelsToShow = Math.max(1, Math.floor(5 / visibleDatasets));
                return Math.min(labelsToShow, totalLabels);
            }
            if (chartSize === 'medium') {
                const labelsToShow = Math.max(1, Math.floor(8 / visibleDatasets));
                return Math.min(labelsToShow, totalLabels);
            }
            if (chartSize === 'large' || chartSize === 'x-large') {
                const targetDataPoints = 25;
                if (totalDataPoints < targetDataPoints) return totalLabels;
                const labelsToShow = Math.max(1, Math.floor(targetDataPoints / visibleDatasets));
                return Math.min(labelsToShow, totalLabels);
            }
            return totalLabels;
        },

        /**
         * Detects if chart uses time-based data by checking group_by value
         */
        isTimeBased() {
            const groupBy = String(this.chart_selected?.group_by?.[0]?.text || '').toLowerCase();
            return groupBy.includes('year') || 
                   groupBy.includes('month') || 
                   groupBy.includes('date') ||
                   groupBy.includes('day');
        },
        
        /**
         * Parse labels to find indices of the most recent year/month/date
         * Returns array of indices that belong to the most recent period
         */
        getMostRecentPeriodIndices() {
            if (!this.isTimeBased()) return [];
            
            const labels = this.chart_selected.labels || [];
            if (labels.length === 0) return [];
            
            // Extract year from labels - could be "2023 (Nov-Oct)" or just "2023"
            const yearPattern = /(\d{4})/;
            const years = labels.map(label => {
                const match = String(label).match(yearPattern);
                return match ? parseInt(match[1]) : null;
            }).filter(y => y !== null);
            
            if (years.length === 0) return [];
            
            // Find the most recent (largest) year
            const mostRecentYear = Math.max(...years);
            
            // Return indices of labels that contain this year
            return labels.map((label, index) => {
                const match = String(label).match(yearPattern);
                return match && parseInt(match[1]) === mostRecentYear ? index : -1;
            }).filter(i => i !== -1);
        },
        
        
        /**
         * Detects if chart uses time-based data by checking group_by value
         */
        isTimeBased() {
            const groupBy = String(this.chart_selected?.group_by?.[0]?.value || '').toLowerCase();
            return groupBy.includes('year') || 
                   groupBy.includes('month') || 
                   groupBy.includes('date') ||
                   groupBy.includes('day');
        },
        
        /**
         * Parse labels to find indices of the most recent year/month/date
         * Returns array of indices that belong to the most recent period
         */
        getMostRecentPeriodIndices() {
            if (!this.isTimeBased()) return [];
            
            const labels = this.chart_selected.labels || [];
            if (labels.length === 0) return [];
            
            // Extract year from labels - could be "2023 (Nov-Oct)" or just "2023"
            const yearPattern = /(\d{4})/;
            const years = labels.map(label => {
                const match = String(label).match(yearPattern);
                return match ? parseInt(match[1]) : null;
            }).filter(y => y !== null);
            
            if (years.length === 0) return [];
            
            // Find the most recent (largest) year
            const mostRecentYear = Math.max(...years);
            
            // Return indices of labels that contain this year
            return labels.map((label, index) => {
                const match = String(label).match(yearPattern);
                return match && parseInt(match[1]) === mostRecentYear ? index : -1;
            }).filter(i => i !== -1);
        },
        
        renderChart() {
            if (!this.chart_selected?.data) return;

            this.num_of_datasets = Object.keys(this.chart_selected.data).length;
            if (this.datasetVisibility && this.datasetVisibility.length !== this.num_of_datasets) {
                this.datasetVisibility = null;
            }
            this.totalDataLength = this.chart_selected.labels?.length || 0;
            this.displayedDataCount = this.forceShowAllData ? this.totalDataLength : this.calculateLabelsToShow();
            this.isDataTruncated = this.displayedDataCount < this.totalDataLength;

            if (this.isTimeBased() && this.isDataTruncated && !this.pageInitialized) {
                this.currentPageStart = Math.max(0, this.totalDataLength - this.displayedDataCount);
            }
            this.pageInitialized = true;

            // Emit state to parent for centralized pagination controls
            this.emitPaginationState();

            // Process percentage if needed (chart-specific)
            if (this.chart_selected.show_series_percentage) {
                this.processValueIntoPrecentage();
            }

            // Get full dataset first (will use displayedDataCount for styling)
            const fullDataset = this.getChartData();
            
            // Apply truncation
            const dataset = this.getTruncatedData(fullDataset);
            
            const options = this.getChartOptions(dataset);

            // First destroy any existing chart
            this.destroyChart();

            // Wait for the next tick to ensure DOM is ready and previous chart is fully destroyed
            this.$nextTick(() => {
                const ctx = document.getElementById('chart-' + this.id);
                if (!ctx?.getContext) {
                    console.warn('Chart canvas not found or context not available');
                    return;
                }

                // Wait until the canvas is visible, then create the chart
                this.createChartWhenVisible(ctx, options, (chartInstance) => {
                    this.chart = markRaw(chartInstance);
                    // Setup mouseleave handler (from mixin)
                    this.setupChartMouseLeave(chartInstance);
                    this.skipAnimationForNextRender = false;
                });
            });
        },
        debouncedRender() {
            if (this.render_timeout) {
                clearTimeout(this.render_timeout);
            }
            this.render_timeout = setTimeout(() => {
                this.$nextTick(() => {
                    this.format_type = this.chart_selected?.metric_formats?.[0] || 'number';
                    this.color = this.chart_selected?.metrics?.[0]?.color || 'yellow';
                    this.num_of_datasets = Object.keys(this.chart_selected.data).length;
                    this.renderChart();
                    this.if_chart_has_data = true;
                    // Re-emit pagination state now that chart has data
                    this.emitPaginationState();
                });
            }, 200); // 200ms debounce
        },
        getBorderRadiusByDataLength(dataLength) {
            if (dataLength < 5) {
                return 10;
            } else if (dataLength < 10) {
                return 7;
            } else if (dataLength < 15) {
                return 5;
            } else {
                return 3;
            }
        },
        /**
         * Calculates a nice rounded-up maximum value for the y-axis
         * Logic: if (top_axis - highest_bar) < 35% of the space between axis ticks, add additional top axis
         */
        /**
         * Calculates bar thickness based on chart size and data length
         */
        processValueIntoPrecentage() {
            const processed_data = {};
            const totals = {};

            // Calculate totals for each dataset
            Object.keys(this.chart_selected.data).forEach((key, i) => {
                totals[i] = Object.values(this.chart_selected.data)[i].reduce((a, b) => a + b, 0);
            });

            // Create new processed data object with percentages
            Object.keys(this.chart_selected.data).forEach((key, j) => {
                processed_data[key] = this.chart_selected.data[key].map(value => 
                    totals[j] ? value / totals[j] : 0
                );
            });

            this.processed_data = processed_data;
        },
        getChartData() {
            var datasetArray = [];
            var element_on_scorecard = document.querySelector('.scorecard-page');
            var max = 0;
            
            // Determine active data length for visual scaling (thickness/radius)
            const activeLabels = this.isDataTruncated ? this.displayedDataCount : (this.chart_selected.labels?.length || 0);
            var data_length = this.num_of_datasets * activeLabels;
            
            const borderRadius = this.getBorderRadiusByDataLength(data_length);
            const barThickness = this.getBarThickness(data_length);

            // Get color indices based on number of datasets
            const colorIndices = this.getColorIndicesForDatasets(this.num_of_datasets);
            const colorArray = this.getColor(this.color);
            // Map indices to actual color values
            const chartColors = colorIndices.map(idx => colorArray[idx]);

            // Use processed_data if available, otherwise use original data
            const dataToUse = this.chart_selected.show_series_percentage ? this.processed_data : this.chart_selected.data;

            if (this.num_of_datasets >1){
                // multiple dataset
                if (this.chart_filter_label.length > 1){ // multiple dimension, should only highlight the selected column within the group
                    this.chart_filter_label_index[0] = this.chart_selected.labels.findIndex(label => label == this.chart_filter_label[0]);
                    this.chart_filter_label_index[1] = Object.keys(dataToUse).findIndex(data => data == this.chart_filter_label[1]);
                }else { //single dimension, should highlight the whole selected column
                    this.chart_filter_label_index[0] = this.chart_selected.labels.findIndex(label => label == this.chart_filter_label[0]);
                }

                Object.keys(dataToUse).forEach((key,index) => {
                    var max_temp = Math.max(...dataToUse[key]);
                    if (max_temp > max){
                        max = max_temp;
                    }
                    // Get color for this dataset (reverse order: last dataset gets first color)
                    const datasetColorIndex = chartColors.length - 1 - index;
                    const datasetColor = chartColors[datasetColorIndex] || chartColors[0];

                    if (this.if_chart_filter_applied){
                        if (this.chart_filter_label.length > 1){
                            var backgroundColorArray = Array(dataToUse[key].length).fill(this.getColorByLetter('gray', 'D'));
                            if(key == this.chart_filter_label[1]){
                                backgroundColorArray[this.chart_filter_label_index[0]] = chartColors[0]; // Use color A
                            }
                            datasetArray.push({
                                label: key==""? '(not set)':key,
                                data: dataToUse[key],
                                backgroundColor: backgroundColorArray,
                                borderWidth: 0,
                                borderRadius: borderRadius,
                                maxBarThickness: barThickness,
                                hidden: this.isDatasetHiddenByDefault(index),
                            });
                        }else {
                            var backgroundColorArray = Array(dataToUse[key].length).fill(this.getColorByLetter('gray', 'D'));
                            backgroundColorArray[this.chart_filter_label_index[0]] = chartColors[0]; // Use color A
                            datasetArray.push({
                                label: key==""? '(not set)':key,
                                data: dataToUse[key],
                                backgroundColor: backgroundColorArray,
                                borderWidth: 0,
                                borderRadius: borderRadius,
                                maxBarThickness: barThickness,
                                hidden: this.isDatasetHiddenByDefault(index),
                            });
                        }
                    } else if(this.chart_selected.highlight_last_value){
                        const isLastDataset = index == this.num_of_datasets - 1;
                        let backgroundColor;
                        if (isLastDataset) {
                            backgroundColor = chartColors[0]; // Use color A
                        } else {
                            // Map index to gray color (reverse: index 0 -> G, index 1 -> F, etc.)
                            const grayIndex = Math.max(0, Math.min(6 - index - 1, 6));
                            const grayLetter = ['G', 'F', 'E', 'D', 'C', 'B', 'A'][grayIndex] || 'A';
                            backgroundColor = this.getColorByLetter('gray', grayLetter);
                        }
                        datasetArray.push({
                            label: key==""? '(not set)':key,
                            data: dataToUse[key],
                            backgroundColor: backgroundColor,
                            borderWidth: 0,
                            borderRadius: borderRadius,
                            maxBarThickness: barThickness,
                            hidden: this.isDatasetHiddenByDefault(index),
                        });
                    }else{
                        datasetArray.push({
                            label: key==""? '(not set)':key,
                            data: dataToUse[key],
                            backgroundColor: datasetColor,
                            borderWidth: 0,
                            borderRadius: borderRadius,
                            maxBarThickness: barThickness,
                            hidden: this.isDatasetHiddenByDefault(index),
                        });
                    }
                });
            } else {
                // single dataset - use color A
                const chartColor = this.getColorByLetter(this.color, 'A');

                Object.keys(dataToUse).forEach(key => {
                    var max_temp = Math.max(...dataToUse[key]);
                    if (max_temp > max){
                        max = max_temp;
                    }
                    var backgroundColorArray = [];

                    if (element_on_scorecard && dataToUse[key].length >=3){
                        backgroundColorArray = Array(dataToUse[key].length).fill(chartColor);
                        for(var i = backgroundColorArray.length - 1; i >= backgroundColorArray.length - 3; i--) {
                            // Extract RGB values from format 'rgb(r g b)' and convert to rgba with 0.5 opacity
                            const rgbMatch = chartColor.match(/rgb\((\d+)\s+(\d+)\s+(\d+)\)/);
                            if (rgbMatch) {
                                backgroundColorArray[i] = `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, 0.5)`;
                            } else {
                                backgroundColorArray[i] = chartColor; // fallback to original color
                            }
                        }
                    } else {
                        if (this.if_benchmark_report){ // benchmark with only 1 dataset means the client's own data is not shown, disregard the highlight_last_value config
                            backgroundColorArray = Array(dataToUse[key].length).fill(chartColor);
                        } else if (this.if_chart_filter_applied){
                            var index = this.chart_selected.labels.findIndex(label => label == this.chart_filter_label[0]);
                            backgroundColorArray = Array(dataToUse[key].length).fill(this.getColorByLetter('gray', 'D'));
                            backgroundColorArray[index] = chartColor;
                        } else if(this.chart_selected.highlight_last_value) {
                            backgroundColorArray = Array(dataToUse[key].length-1).fill(this.getColorByLetter('gray', 'D'));
                            backgroundColorArray.push(chartColor);
                        } else {
                            backgroundColorArray = Array(dataToUse[key].length).fill(chartColor);
                        }
                    }

                    datasetArray.push({
                        label: key==""? '(not set)':key,
                        data: dataToUse[key],
                        backgroundColor: backgroundColorArray,
                        borderWidth: 0,
                        borderRadius: borderRadius,
                        barThickness: barThickness,
                    });
                });
            }

            var labels_temp = [];
            this.chart_selected.labels.forEach((label)=>{
                if (label){
                    labels_temp.push(label)
                }else{
                    labels_temp.push('(not set)')
                }
            });

            this.max_value = max;
            return {
                labels: labels_temp,
                datasets: datasetArray
            };
        },
        /**
         * Darkens a color by a percentage (0-1)
         * @param {string} color - RGB color string like 'rgb(255 255 255)'
         * @param {number} percent - Percentage to darken (0.2 = 20%)
         * @returns {string} Darkened RGB color string
         */
        /**
         * Animates hover opacity from current value to target value
         * @param {number} targetOpacity - Target opacity (0 to 1)
         * @param {Object} chart - Chart.js instance
         */
        getChartOptions(data) {
            var self = this;
            
            // Custom plugin to draw solid line at y=0 (base x-axis) extending across chart area including x-axis labels
            const solidBaseLinePlugin = {
                id: 'solidBaseLine',
                afterDraw: (chart) => {
                    const ctx = chart.ctx;
                    const yScale = chart.scales.y;
                    const xScale = chart.scales.x;
                    
                    // Get the y position for zero
                    const zeroY = yScale.getPixelForValue(0);
                    
                    // Use chart area boundaries and extend slightly to cover x-axis labels
                    // Start from the left edge of the chart area (where bars start)
                    // End at the right edge of the chart area, extending slightly to cover labels
                    const leftEdge = chart.chartArea.left;
                    const rightEdge = xScale.right; // Extend to cover x-axis labels
                    
                    // Draw solid line extending across the chart area and x-axis labels
                    ctx.save();
                    ctx.strokeStyle = 'rgba(206, 219, 215, 1)';
                    ctx.lineWidth = 1;
                    ctx.setLineDash([]); // Solid line - empty array means no dashes
                    ctx.beginPath();
                    ctx.moveTo(leftEdge, zeroY);
                    ctx.lineTo(rightEdge, zeroY);
                    ctx.stroke();
                    ctx.restore();
                }
            };
            
            // Custom plugin to draw background color for hovered group
            const hoverBackgroundPlugin = {
                id: 'hoverBackground',
                beforeDatasetsDraw: (chart) => {
                    if (self.hoveredIndex !== null && chart.scales.x) {
                        const ctx = chart.ctx;
                        const xScale = chart.scales.x;
                        const yScale = chart.scales.y;
                        
                        // Get shade E of the metric color with 60% opacity
                        const colorE = self.getColorByLetter(self.color, 'E');
                        const rgbMatch = colorE.match(/rgb\((\d+)\s+(\d+)\s+(\d+)\)/);
                        
                        if (rgbMatch) {
                            const r = rgbMatch[1];
                            const g = rgbMatch[2];
                            const b = rgbMatch[3];
                            const rgbaColor = `rgba(${r}, ${g}, ${b}, ${0.3 * self.hoverOpacity})`;
                            
                            // Get the pixel positions for the hovered category and adjacent categories
                            const hoveredPixelValue = xScale.getPixelForValue(self.hoveredIndex);
                            const totalWidth = xScale.width;
                            const numCategories = xScale.ticks.length;
                            const categorySpacing = totalWidth / numCategories;
                            
                            // Calculate left edge: midpoint between previous category and hovered category
                            let leftEdge;
                            if (self.hoveredIndex > 0) {
                                const prevPixelValue = xScale.getPixelForValue(self.hoveredIndex - 1);
                                leftEdge = (prevPixelValue + hoveredPixelValue) / 2;
                            } else {
                                // For first category, use midpoint between start and current
                                leftEdge = hoveredPixelValue - categorySpacing / 2;
                            }
                            
                            // Calculate right edge: midpoint between hovered category and next category
                            let rightEdge;
                            if (self.hoveredIndex < numCategories - 1) {
                                const nextPixelValue = xScale.getPixelForValue(self.hoveredIndex + 1);
                                rightEdge = (hoveredPixelValue + nextPixelValue) / 2;
                            } else {
                                // For last category, use midpoint between current and end
                                rightEdge = hoveredPixelValue + categorySpacing / 2;
                            }
                            
                            // Clamp to chart area so overlay never covers y-axis or x-axis labels
                            const chartLeft = chart.chartArea.left;
                            const chartRight = chart.chartArea.right;
                            const leftEdgeClipped = Math.max(leftEdge, chartLeft);
                            const rightEdgeClipped = Math.min(rightEdge, chartRight);
                            
                            // Calculate x position and width for the wider highlight box
                            const x = leftEdgeClipped;
                            const categoryWidth = rightEdgeClipped - leftEdgeClipped;
                            const y = yScale.top;
                            
                            // Extend height to include axis labels
                            // Get the bottom of the x-axis scale which includes the label area
                            const xAxisBottom = xScale.bottom || chart.chartArea.bottom;
                            const height = xAxisBottom - yScale.top;
                            
                            // Draw rounded rectangle with 30px border radius
                            ctx.save();
                            ctx.fillStyle = rgbaColor;
                            const radius = 30;
                            ctx.beginPath();
                            ctx.moveTo(x + radius, y);
                            ctx.lineTo(x + categoryWidth - radius, y);
                            ctx.quadraticCurveTo(x + categoryWidth, y, x + categoryWidth, y + radius);
                            ctx.lineTo(x + categoryWidth, y + height - radius);
                            ctx.quadraticCurveTo(x + categoryWidth, y + height, x + categoryWidth - radius, y + height);
                            ctx.lineTo(x + radius, y + height);
                            ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
                            ctx.lineTo(x, y + radius);
                            ctx.quadraticCurveTo(x, y, x + radius, y);
                            ctx.closePath();
                            ctx.fill();
                            ctx.restore();
                        }
                    }
                }
            };
            
            // Custom plugin to darken hovered bars
            // Uses afterDatasetsDraw (not afterDraw) so datalabels plugin draws on top when it runs after this
            const darkenBarsPlugin = {
                id: 'darkenBars',
                afterDatasetsDraw: (chart) => {
                    if (self.hoveredIndex !== null) {
                        const ctx = chart.ctx;
                        const meta = chart.getDatasetMeta(0);
                        
                        // Iterate through all datasets
                        chart.data.datasets.forEach((dataset, datasetIndex) => {
                            const meta = chart.getDatasetMeta(datasetIndex);
                            if (!meta || meta.hidden) return;
                            
                            // Get the bar element for the hovered index
                            const bar = meta.data[self.hoveredIndex];
                            if (!bar) return;
                            
                            // Get the original color
                            const originalColor = Array.isArray(dataset.backgroundColor)
                                ? dataset.backgroundColor[self.hoveredIndex]
                                : dataset.backgroundColor;
                            
                            // Darken the color by 10%
                            const darkerColor = self.darkenColor(originalColor, 0.1);
                            
                            // Get bar properties
                            const { x, y, base, width, height } = bar.getProps(['x', 'y', 'base', 'width', 'height']);
                            
                            // Get border radius from the dataset
                            const borderRadius = dataset.borderRadius || 0;
                            
                            // Calculate actual border radius value
                            const actualRadius = Math.min(borderRadius, width / 2, Math.abs(height) / 2);
                            
                            // Draw the darkened bar on top
                            ctx.save();
                            ctx.fillStyle = darkerColor;
                            
                            // Draw rounded rectangle (only top corners rounded)
                            const barHeight = y - base;
                            const barY = Math.min(y, base);
                            const barX = x - width / 2;
                            const barBottom = barY + Math.abs(barHeight);
                            
                            ctx.beginPath();
                            // Start at top-left (rounded)
                            ctx.moveTo(barX + actualRadius, barY);
                            // Top edge
                            ctx.lineTo(barX + width - actualRadius, barY);
                            // Top-right corner (rounded)
                            ctx.quadraticCurveTo(barX + width, barY, barX + width, barY + actualRadius);
                            // Right edge (straight to bottom)
                            ctx.lineTo(barX + width, barBottom);
                            // Bottom-right corner (sharp)
                            ctx.lineTo(barX, barBottom);
                            // Bottom-left corner (sharp)
                            ctx.lineTo(barX, barY + actualRadius);
                            // Top-left corner (rounded)
                            ctx.quadraticCurveTo(barX, barY, barX + actualRadius, barY);
                            ctx.closePath();
                            ctx.fill();
                            ctx.restore();
                        });
                    }
                }
            };
            
            var config = {
                plugins: [solidBaseLinePlugin, hoverBackgroundPlugin, darkenBarsPlugin, ChartDataLabels],
                type: 'bar',
                data: data,

                //All of the special aspects that make these charts unique
                options: {
                    // onClick: (e) => {
                    //     const canvasPosition = getRelativePosition(e, this.chart);

                    //     // Substitute the appropriate scale IDs
                    //     const dataX = this.chart.scales.x.getValueForPixel(canvasPosition.x);
                    //     const dataY = this.chart.scales.y.getValueForPixel(canvasPosition.y);
                    //     console.log(dataX, dataY);
                    // },
                    animation: {
                        duration: this.skipAnimationForNextRender ? 0 : 500,
                    },
                    interaction: {
                        intersect: false,
                        mode: 'index', 
                    },
                    onHover: (event, activeElements, chart) => {
                        if (activeElements && activeElements.length > 0) {
                            // Clear any pending hide timeout when hovering over an element
                            if (this.hideTimeout) {
                                clearTimeout(this.hideTimeout);
                                this.hideTimeout = null;
                            }
                            
                            const activeElement = activeElements[0];
                            const dataIndex = activeElement.index;
                            
                            // If hovering over a different data point OR returning to a point that's not currently hovered,
                            // (re)start the debounce timer
                            if (dataIndex !== this.pendingHoverIndex || this.hoveredIndex !== dataIndex) {
                                // Cancel any pending show timeout
                                if (this.hoverTimeout) {
                                    clearTimeout(this.hoverTimeout);
                                }
                                
                                // Reset current state immediately when moving to a new point
                                // to keep highlight and tooltip in sync (both hidden during the debounce)
                                if (this.hoveredIndex !== null && this.hoveredIndex !== dataIndex) {
                                    this.hoveredIndex = null;
                                    this.hoverOpacity = 0;
                                    chart.update('none');
                                }
                                
                                // Reset flags for new data point
                                this.pendingHoverIndex = dataIndex;
                                
                                // Start new debounce timer - 200ms delay to show hover
                                this.hoverTimeout = setTimeout(() => {
                                    this.hoverTimeout = null;
                                    // Apply hover effect after delay
                                    if (this.hoveredIndex !== dataIndex) {
                                        this.hoveredIndex = dataIndex;
                                        chart.update('none'); // Immediate update for bar colors and tooltip
                                        this.animateHoverOpacity(1, chart);
                                    }
                                }, 200);
                            }
                        } else {
                            // Clear any pending show timeout when leaving
                            if (this.hoverTimeout) {
                                clearTimeout(this.hoverTimeout);
                                this.hoverTimeout = null;
                                this.pendingHoverIndex = null;
                            }
                            
                            // Debounce hiding the hover state (200ms delay)
                            // This matches the show debounce timing for consistency
                            if (this.hoveredIndex !== null && !this.hideTimeout) {
                                this.hideTimeout = setTimeout(() => {
                                    // Only hide if still not hovering and tooltip is also not being hovered
                                    if (this.hoveredIndex !== null && !this.isTooltipHovered) {
                                        this.hoveredIndex = null;
                                        chart.update('none'); // Immediate update for bar colors and tooltip
                                        this.animateHoverOpacity(0, chart);

                                        // Explicitly hide tooltip element to stay in sync
                                        let tooltipEl = document.getElementById('chart-' + this.id + '-' + this.effectiveSectionId + '-tooltip');
                                        if (tooltipEl) {
                                            tooltipEl.style.opacity = 0;
                                            tooltipEl.style.pointerEvents = 'none';
                                        }
                                    }
                                    this.hideTimeout = null;
                                }, 200);
                            }
                        }
                    },
                    responsive: true,
                    animations: {
                        x: {
                            duration: 500,
                        }
                    },
                    maintainAspectRatio: false,
                    datasets: {
                        bar: {
                            barPercentage: 0.6,
                            categoryPercentage: 0.8,
                        }
                    },
                    layout: {
                        padding: {
                            right: 50,
                            left: 10,
                            bottom: 10
                        }
                    },
                    indexAxis: 'x',
                    scales: {
                        x: {
                            //If the type is bar-stacked, it will make it a stacked bar chart
                            stacked: false,
                            grid: {
                                drawBorder: true,
                                display : false,
                                borderDash: [],
                                borderColor: 'rgba(206, 219, 215, 1)',
                                borderWidth: 1,
                            },
                            border: {
                                display: true,
                                color: 'rgba(206, 219, 215, 1)',
                                width: 1,
                                dash: [],
                            },
                            ticks: {
                                font: {
                                    size: this.font_size,
                                    weight: 400,
                                    family: "'Nunito', sans-serif",
                                },
                                maxRotation:0,
                                autoSkip:false,
                                callback: function(value, index, ticks, context) {
                                    // Get the label text from the truncated data (data.labels contains the sliced labels)
                                    let labelText = '';
                                    if(data.labels && data.labels[value]){
                                        labelText = data.labels[value] + "";
                                    } else {
                                        labelText = '(not set)';
                                    }
                                    
                                    // Break label into multiple lines to prevent collisions
                                    // Maximum characters per line (adjust based on font size and chart width)
                                    const maxCharsPerLine = 12;
                                    const words = labelText.split(" ");
                                    const lines = [];
                                    let currentLine = '';
                                    
                                    words.forEach((word, wordIndex) => {
                                        // If adding this word would exceed the limit, start a new line
                                        const testLine = currentLine ? currentLine + ' ' + word : word;
                                        
                                        if (testLine.length > maxCharsPerLine && currentLine) {
                                            // Save current line and start a new one
                                            lines.push(currentLine);
                                            currentLine = word;
                                        } else {
                                            // Add word to current line
                                            currentLine = testLine;
                                        }
                                        
                                        // If this is the last word, add the current line
                                        if (wordIndex === words.length - 1 && currentLine) {
                                            lines.push(currentLine);
                                        }
                                    });
                                    
                                    // Return array of lines (Chart.js will render each as a separate line)
                                    // If only one line, return as single-element array for consistency
                                    return lines.length > 0 ? lines : [labelText];
                                }
                            }
                        },
                        y: {
                            display: true,
                            position: 'left',
                            beginAtZero: true,
                            stacked: false,
                            // Ensure there's always space above the highest column
                            max: this.getYAxisMax(this.max_value),
                            //For the ticks on the axes, format the numbers to make the pretty
                            border: {
                                display: false,
                                width: 2,
                                dash: [5,5],
                                dashOffset: 0,
                            },
                            grid: {
                                drawOnChartArea: true,
                                drawBorder: false,

                                color: 'rgba(206, 219, 215, 1)',
                                lineWidth: 1, // gridline thickness
                                borderDash: [2,2], // gridline in the chart area that is perpendicular to this axis

                                drawTicks: true,
                                tickColor: 'rgba(206, 219, 215, 1)',
                                tickLength: 20,
                                tickBorderDash: [2,2],
                                tickBorderDashOffset: 0,
                                z: -2,
                            },
                            ticks: {
                                font: {
                                    size: this.font_size,
                                    weight: 400,
                                    family: "'Nunito', sans-serif",
                                },
                                autoSkip: true,
                                maxTicksLimit: 5,
                                callback: function(value, index, ticks, context) {

                                    if(self.chart_selected.show_series_percentage){
                                        return self.percentage(value);
                                    }
                                    if(self.format_type == 'money') {
                                        if(self.show_compressed_numbers && value >= 1000) {
                                            return self.currency_abbr(value);
                                        }
                                        return self.currency_with_zero(value);
                                    }
                                    if(self.format_type == 'number') {
                                        if(self.show_compressed_numbers && value >= 1000) {
                                            return self.num_abbr(value);
                                        }
                                        if(self.max_value >= 10) {
                                            return self.number_with_zero(value);
                                        }
                                        return self.number_with_decimal(value);
                                    }
                                    if(self.format_type == 'percent')
                                        return self.percentage(value);
                                }
                            }
                        }
                    },
                    plugins: {
                        legend: {
                            //Only show the legend if there is more than one media type
                            position: (data.datasets.length > 1) ? 'bottom' : 'none',
                            align: 'center',
                            fullSize: true,
                            onClick: (e, legendItem, legend) => {
                                const chart = legend.chart;
                                const datasetIndex = legendItem.datasetIndex;

                                chart.setDatasetVisibility(datasetIndex, !chart.isDatasetVisible(datasetIndex));
                                chart.update();
                                const self = this;
                                self.datasetVisibility = chart.data.datasets.map((_, i) => chart.isDatasetVisible(i));
                            },
                            onHover: function(e, chart) {
                                e.native.target.style.cursor = 'pointer';
                            },
                            onLeave: function(e, chart) {
                                e.native.target.style.cursor = 'default';
                            },
                            labels: {
                                usePointStyle: true,
                                pointStyle: 'circle',
                                padding: 20,
                                generateLabels: (chart) => {
                                    return chart.data.datasets.map((dataset, index) => {
                                        const fill = Object.keys(this.chart_selected.data).length > 1
                                        ? dataset.backgroundColor
                                        : dataset.backgroundColor.slice(-1);

                                        return {
                                            text: dataset.label,
                                            datasetIndex: index,                 // ✅ required for clicking
                                            hidden: !chart.isDatasetVisible(index), // ✅ required for correct strikethrough/opacity
                                            pointStyle: 'circle',
                                            fillStyle: fill,
                                            lineWidth: 0,
                                        };
                                    }); 
                                },
                                font: {
                                    size: this.font_size,
                                    weight: 400,
                                    family: "'Nunito', sans-serif",
                                },
                            }
                        },
                        tooltip:  {
                            enabled: false,
                            xAlign: 'center',
                            external: function(context) {
                                const chartCanvasId = context.chart?.canvas?.id;
                                if (!chartCanvasId || !chartCanvasId.startsWith('chart-')) return;

                                const chartIdFromCanvas = chartCanvasId.replace(/^chart-/, '');
                                const expectedTooltipId = self.getTooltipId();
                                if (chartIdFromCanvas !== String(self.id)) {
                                    let tooltipEl = document.getElementById(expectedTooltipId);
                                    if (tooltipEl) {
                                        tooltipEl.style.opacity = 0;
                                        tooltipEl.style.pointerEvents = 'none';
                                    }
                                    return;
                                }

                                let tooltipEl = self.getOrCreateTooltipElement();
                                if (!tooltipEl) return;

                                const tooltipIdPrefix = 'chart-' + chartIdFromCanvas + '-';
                                if (!tooltipEl.id.startsWith(tooltipIdPrefix) || !tooltipEl.id.endsWith('-tooltip')) {
                                    tooltipEl.style.opacity = 0;
                                    tooltipEl.style.pointerEvents = 'none';
                                    return;
                                }
                                if (tooltipEl.id !== expectedTooltipId) {
                                    tooltipEl.style.opacity = 0;
                                    tooltipEl.style.pointerEvents = 'none';
                                    return;
                                }

                                const tooltipModel = context.tooltip;

                                if (!tooltipModel || tooltipModel.opacity === 0) {
                                    if (!self.isTooltipHovered && self.hoveredIndex === null && tooltipEl) {
                                        tooltipEl.style.opacity = 0;
                                        tooltipEl.style.pointerEvents = 'none';
                                        self.hideAllChartTooltips();
                                    }
                                    return;
                                }
                                
                                const currentDataIndex = tooltipModel.dataPoints && tooltipModel.dataPoints.length > 0 
                                    ? tooltipModel.dataPoints[0].dataIndex 
                                    : null;
                                
                                if (currentDataIndex === null || currentDataIndex !== self.hoveredIndex) {
                                    tooltipEl.style.opacity = 0;
                                    tooltipEl.style.pointerEvents = 'none';
                                    self.hideAllChartTooltips();
                                    return;
                                }

                                // Determine which group is hovered for smart positioning
                                const dataIndex = tooltipModel.dataPoints && tooltipModel.dataPoints.length > 0 
                                    ? tooltipModel.dataPoints[0].dataIndex 
                                    : null;
                                
                                // Hide tooltips from other sections using mixin method
                                self.hideOtherTooltips(tooltipEl);

                                // Apply base styles using mixin method
                                self.applyTooltipBaseStyles(tooltipEl);

                                // Set Text
                                if (tooltipModel.body) {
                                    const titleLines = tooltipModel.title || [];
                                    const bodyLines = tooltipModel.body.map(b => b.lines);

                                    let innerHtml = '<thead>';
                                    innerHtml += '<tr><th style="font-weight: 500; font-size: 18px; padding-bottom: 4px;">' + titleLines[0] + '</th></tr>';
                                    innerHtml += '</thead><tbody>';

                                    let tooltipBody = '';

                                    bodyLines.forEach((body, i) => {
                                        if (!body || !body[0]) return;

                                        const body_label_temp = body[0].split(':');
                                        const body_label = body_label_temp.slice(0, body_label_temp.length - 1).join(':');
                                        const value = tooltipModel.dataPoints[i]?.raw;

                                        if (value === undefined) return;

                                        const colors = tooltipModel.labelColors[i];
                                        const textColor = colors.backgroundColor;

                                        let formattedValue = '';
                                        if (self.num_of_datasets > 1) {
                                            if (self.chart_selected?.show_series_percentage) {
                                                formattedValue = self.percentage(value);
                                            } else {
                                                if (self.format_type === 'money' && self.max_value >= 10) {
                                                    formattedValue = self.currency_with_zero(value);
                                                } else if (self.format_type === 'money' && self.max_value < 10) {
                                                    formattedValue = self.currency_with_decimal(value);
                                                } else if (self.format_type === 'number' && self.max_value >= 10) {
                                                    formattedValue = self.number_with_zero(value);
                                                } else if (self.format_type === 'number' && self.max_value < 10) {
                                                    formattedValue = self.number_with_decimal(value);
                                                } else if (self.format_type === 'percent') {
                                                    formattedValue = self.percentage(value);
                                                }
                                            }
                                            const individualFilterButton = !self.if_benchmark_report
                                                ? '<button id="'+ i +'" class="btn btn-sm btn-white tooltip-btn-white btn-explore-individual p-0 ms-2" style="width:24px !important; height: 24px !important; border-radius: 50% !important; display: flex !important; align-items: center !important; justify-content: center !important;"><img class="icon" src="/img/icons/avid-icons/filter-funnel-01.svg" style="width: 14px; height: 14px; margin: 0 !important;" alt=""></button>'
                                                : '';
                                            tooltipBody += '<tr><td class="d-flex align-items-center" style="padding-top: 1px; padding-bottom: 1px;"><span style="color: ' + textColor + '; font-weight: 400;">' + body_label + '</span>: <span style="color: #111921; font-weight: 400; margin-left: 4px;">' + formattedValue + '</span>' + individualFilterButton + '</td></tr>';
                                        } else {
                                            if (self.chart_selected?.show_series_percentage) {
                                                formattedValue = self.percentage(value);
                                            } else {
                                                if (self.format_type === 'money' && self.max_value >= 10) {
                                                    formattedValue = self.currency_with_zero(value);
                                                } else if (self.format_type === 'money' && self.max_value < 10) {
                                                    formattedValue = self.currency_with_decimal(value);
                                                } else if (self.format_type === 'number' && self.max_value >= 10) {
                                                    formattedValue = self.number_with_zero(value);
                                                } else if (self.format_type === 'number' && self.max_value < 10) {
                                                    formattedValue = self.number_with_decimal(value);
                                                } else if (self.format_type === 'percent') {
                                                    formattedValue = self.percentage(value);
                                                }
                                            }
                                            tooltipBody += '<tr><td style="padding-top: 1px; padding-bottom: 1px;"><span class="tooltip-single-dataset-value" style="color: #111921; font-weight: 400; font-size: 32px;">' + formattedValue + '</span></td></tr>';
                                        }
                                    });

                                    innerHtml += tooltipBody;

                                    if (self.if_on_report_page && !self.if_benchmark_report) {
                                        const isFilterApplied = self.if_chart_filter_applied && 
                                                             self.chart_filter_label?.[0] === titleLines[0] &&
                                                             (!self.chart_filter_label?.[1] || 
                                                              self.chart_filter_label[1] === bodyLines[0]?.[0]?.split(':')[0]);

                                        innerHtml += '<div class="d-flex justify-content-end my-2">' +
                                            '<button class="btn btn-sm btn-primary d-flex align-items-center px-4" style="font-weight: 500;">' +
                                            '<img class="icon me-1" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">' + (isFilterApplied ? 'Remove as Filter' : 'Filter') +
                                            '</button>' +
                                            '</div>';

                                        innerHtml += '</tbody>';

                                        const tableRoot = tooltipEl.querySelector('table');
                                        if (tableRoot) {
                                            tableRoot.innerHTML = innerHtml;

                                            // Handle individual explore buttons (only for multiple datasets)
                                            if (self.num_of_datasets > 1) {
                                                const btnExploreIndividuals = tooltipEl.querySelectorAll('.btn-explore-individual');
                                                if (self.if_chart_filter_applied) {
                                                    // Disable individual buttons when filter is applied
                                                    btnExploreIndividuals.forEach((btn) => {
                                                        btn.disabled = true;
                                                    });
                                                } else {
                                                    // Show and enable individual buttons when no filter is applied
                                                    btnExploreIndividuals.forEach((btn) => {
                                                        btn.hidden = false;
                                                        btn.disabled = false;
                                                        btn.style.pointerEvents = 'auto';
                                                        btn.style.opacity = '1';
                                                        
                                                        // Disable if chart filter is applied on another chart
                                                        const isFilterOnOtherChart = self.applied_chart_filter?.if_chart_filter_applied && 
                                                                                     !self.if_chart_filter_applied;
                                                        btn.disabled = isFilterOnOtherChart;
                                                        
                                                        if (!btn.disabled) {
                                                            btn.addEventListener('click', (event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                                
                                                                self.if_chart_filter_applied = true;
                                                                self.chart_filter_label = [];

                                                                const filter_array = [];
                                                                const datasetIndex = parseInt(event.currentTarget.id);
                                                                
                                                                // Build filters for both dimensions (x-axis category and dataset)
                                                                self.chart_selected?.group_by?.forEach((dimension, index) => {
                                                                    let value_temp = null;
                                                                    
                                                                    if (index === 0) {
                                                                        // First dimension: use the x-axis category (title)
                                                                        value_temp = titleLines[0];
                                                                    } else if (index === 1) {
                                                                        // Second dimension: use the dataset label from the clicked row
                                                                        const bodyLine = bodyLines[datasetIndex]?.[0];
                                                                        if (bodyLine) {
                                                                            const body_label_temp = bodyLine.split(':');
                                                                            value_temp = body_label_temp.slice(0, body_label_temp.length - 1).join(':').trim();
                                                                        }
                                                                    }
                                                                    
                                                                    if (value_temp) {
                                                                        self.chart_filter_label.push(value_temp);
                                                                        filter_array.push({
                                                                            name: dimension.text,
                                                                            column: dimension.value,
                                                                            value: value_temp
                                                                        });
                                                                    }
                                                                });

                                                                // Only emit if we have at least one filter
                                                                if (filter_array.length > 0) {
                                                                    self.$emit('exploreChart', {
                                                                        chart_selected: self.chart_selected,
                                                                        chart_filters: filter_array
                                                                    });
                                                                }
                                                            });
                                                        }
                                                    });
                                                }
                                            }

                                            // Find the main Explore button (not the individual explore buttons for each dataset)
                                            const btn = tooltipEl.querySelector('.btn-primary');
                                            if (btn) {
                                                // Disable button if chart filter is applied on another chart
                                                const isFilterOnOtherChart = self.applied_chart_filter?.if_chart_filter_applied && 
                                                                             !self.if_chart_filter_applied;
                                                
                                                // Disable button if filter is applied on this chart and hovering over a different bar/dataset
                                                const isFilterOnThisChart = self.if_chart_filter_applied;
                                                let isDifferentSelection = false;
                                                
                                                if (isFilterOnThisChart) {
                                                    // Check first dimension (x-axis category)
                                                    const hoveredBar = titleLines[0];
                                                    const filteredBar = self.chart_filter_label?.[0];
                                                    
                                                    if (hoveredBar !== filteredBar) {
                                                        isDifferentSelection = true;
                                                    } else if (self.chart_filter_label.length > 1) {
                                                        // For two-dimensional filters, also check second dimension (dataset)
                                                        // Check if the filtered dataset matches any of the body lines
                                                        const filteredDataset = self.chart_filter_label[1];
                                                        const hasMatchingDataset = bodyLines.some(line => {
                                                            if (line && line[0]) {
                                                                const body_label_temp = line[0].split(':');
                                                                const datasetLabel = body_label_temp.slice(0, body_label_temp.length - 1).join(':').trim();
                                                                return datasetLabel === filteredDataset;
                                                            }
                                                            return false;
                                                        });
                                                        
                                                        if (!hasMatchingDataset) {
                                                            isDifferentSelection = true;
                                                        }
                                                    }
                                                }
                                                
                                                btn.disabled = isFilterOnOtherChart || (isFilterOnThisChart && isDifferentSelection);

                                                if (self.if_chart_filter_applied) {
                                                    btn.addEventListener('click', () => {
                                                        self.if_chart_filter_applied = false;
                                                        self.chart_filter_label = [];
                                                        self.$emit('exploreChart', {
                                                            chart_selected: null,
                                                            chart_filters: []
                                                        });
                                                        // const dataset = self.getChartData();
                                                        // if (self.chart) {
                                                        //     self.chart.data = dataset;
                                                        //     self.chart.update();
                                                        // }
                                                    });
                                                } else {
                                                    btn.addEventListener('click', (event) => {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        
                                                        self.if_chart_filter_applied = true;
                                                        self.chart_filter_label = [];

                                                        const filter_array = [];
                                                        // For the main Explore button, only filter by the first dimension (the group/category)
                                                        // This works the same way for both single and multiple datasets
                                                        // Individual dataset filtering would be handled by individual explore buttons (if implemented)
                                                        self.chart_selected?.group_by?.forEach((dimension, index) => {
                                                            // Only use the first dimension (x-axis category/group)
                                                            if (index === 0) {
                                                                const value_temp = titleLines[0];
                                                                if (value_temp) {
                                                                    self.chart_filter_label.push(value_temp);
                                                                    filter_array.push({
                                                                        name: dimension.text,
                                                                        column: dimension.value,
                                                                        value: value_temp
                                                                    });
                                                                }
                                                            }
                                                            // Skip second dimension for main Explore button - it filters the entire group
                                                        });

                                                        // Only emit if we have at least one filter
                                                        if (filter_array.length > 0) {
                                                            self.$emit('exploreChart', {
                                                                chart_selected: self.chart_selected,
                                                                chart_filters: filter_array
                                                            });
                                                        }
                                                    });
                                                }
                                            }
                                        }
                                    } else {
                                        innerHtml += '</tbody>';
                                        const tableRoot = tooltipEl.querySelector('table');
                                        if (tableRoot) {
                                            tableRoot.innerHTML = innerHtml;
                                        }
                                    }
                                }
                                
                                // Position tooltip using mixin method
                                self.positionTooltipOnSide(tooltipEl, context, dataIndex);
                            }
                        },
                        datalabels: {
                            anchor: function(context) {
                                const value = context.dataset.data[context.dataIndex];
                                if (value == null) return 'center';
                                return value >= 0 ? 'end' : 'start';
                            },
                            align: function(context) {
                                const value = context.dataset.data[context.dataIndex];
                                if (value == null) return 'center';
                                return value >= 0 ? 'end' : 'start';
                            },
                            offset: -2, // Reduce space between label and end of bar
                            color: function(context) {
                                const baseColor = '#5f687c';
                                // If labels are always shown, return full opacity
                                if (self.if_show_label) {
                                    return baseColor;
                                }
                                // For hover labels, apply animated opacity
                                if (self.hoveredIndex !== null && context.dataIndex === self.hoveredIndex) {
                                    return `rgba(17, 25, 33, ${self.hoverOpacity})`;
                                }
                                return baseColor;
                            },
                            display: function(context) {
                                // When toggle is ON: show labels only for visible datasets (respect legend-hidden state)
                                if (self.if_show_label) {
                                    const meta = context.chart.getDatasetMeta(context.datasetIndex);
                                    return meta && !meta.hidden;
                                }
                                
                                // Show labels for hovered group
                                if (self.hoveredIndex !== null && context.dataIndex === self.hoveredIndex) {
                                    return true;
                                }
                                
                                // Use shouldShowDataLabels() to determine base visibility
                                if (!self.shouldShowDataLabels()) {
                                    return false;
                                }
                                
                                // Additional logic for time-based charts with > 15 data points
                                // When shouldShowDataLabels() returns true but we have > 15 points,
                                // show labels only for the most recent year
                                const chartSize = self.size || self.chart_selected?.size || 'small';
                                const totalLabels = self.chart_selected.labels?.length || 0;
                                
                                if ((chartSize === 'large' || chartSize === 'x-large') && self.isTimeBased() && totalLabels > 15) {
                                    const mostRecentIndices = self.getMostRecentPeriodIndices();
                                    // Adjust indices based on currentPageStart for truncated data
                                    const adjustedIndices = mostRecentIndices.map(idx => idx - self.currentPageStart);
                                    return adjustedIndices.includes(context.dataIndex);
                                }
                                
                                // For all other cases where shouldShowDataLabels() returns true, show the label
                                return true;
                            },
                            font: {
                                weight: 'regular',
                                size: this.font_size,
                            },
                            formatter: function(value, context) {
                                // When hovering, show labels for all datasets at the hovered index
                                if (self.hoveredIndex !== null && context.dataIndex === self.hoveredIndex) {
                                    return self.formatDataLabelValue(
                                        value, 
                                        self.format_type, 
                                        context.datasetIndex,
                                        self.chart_selected.show_series_percentage
                                    );
                                }

                                if (self.if_chart_filter_applied && self.applied_chart_filter.chart_filters.length){
                                    if (self.chart_filter_label.length > 1){
                                        if(context.dataIndex != self.chart_filter_label_index[0] || context.datasetIndex != self.chart_filter_label_index[1])
                                            return null;
                                    } else {
                                        if(context.dataIndex != self.chart_filter_label_index[0])
                                            return null;
                                    }
                                } else {
                                    // Show labels for hovered group even if show_all_data_labels is false
                                    if(self.hoveredIndex !== null && context.dataIndex === self.hoveredIndex) {
                                        // Show label for hovered group
                                    } else if(!self.chart_selected.show_all_data_labels && self.chart_selected.highlight_last_value && context.datasetIndex != Object.keys(self.chart_selected.data).length -1){
                                        return null;
                                    }
                                }

                                return self.formatDataLabelValue(
                                    value, 
                                    self.format_type, 
                                    context.datasetIndex,
                                    self.chart_selected.show_series_percentage
                                );
                            },
                        }
                    },
                    title: {
                        display:false,
                    },
                }
           };
           return config;
       }
   }
});
</script>